import {
    DocumentTeamMemberDto,
    DocumentTeamMemberRoleEnum,
    DocumentWorkflowActionEnum,
    DocumentWorkflowActionStatusEnum,
    DocumentWorkflowStageDto,
    DocumentWorkflowStageStatusEnum,
    DocumentWorkflowStageTypeEnum,
} from "./documentWorkflowApi"
import {
    AvailableWorkflowUser,
    TeamMemberRole,
    UiStageType,
    WorkflowMemberViewModel,
} from "./documentWorkflowTypes"

export type UiStageStatus =
    | "inProgress"
    | "completed"
    | "pending"
    | "cancelled"
    | "unknown"

export type UiStageStatusInfo = {
    kind: UiStageStatus
    label: string
    color: "default" | "info" | "success" | "warning" | "error"
    variant: "filled" | "outlined"
}

export type UiWorkflowActionStatus =
    | "waiting"
    | "inProgress"
    | "completed"
    | "rejected"
    | "cancelled"
    | "unknown"

export type UiWorkflowActionStatusInfo = {
    kind: UiWorkflowActionStatus
    label: string
    color: "default" | "info" | "success" | "warning" | "error"
    waiting: boolean
}

type StageActionData = {
    actionType?: DocumentWorkflowActionEnum | null
    actionStatus?: DocumentWorkflowActionStatusEnum | null
    actionExecDate?: string | null
    actionDeadline?: string | null
}

const stageTypeLookup: Record<string, UiStageType> = {
    EDIT: "EDIT",
    EDITION: "EDIT",
    REVIEW: "REVIEW",
    OPINION: "REVIEW",
    APPROVAL: "APPROVAL",
    APPROVE: "APPROVAL",
}

const stageTypeByNumber: Record<number, UiStageType> = {
    0: "EDIT",
    1: "REVIEW",
    2: "APPROVAL",
}

const roleLookup: Record<string, TeamMemberRole> = {
    AUTHOR: "Autor",
    CREATOR: "Autor",
    EDITOR: "Edytor",
    REVIEWER: "Opiniujacy",
    APPROVER: "Zatwierdzajacy",
    REVIEWERANDAPPROVER: "Zatwierdzajacy",
}

const roleByNumber: Record<number, TeamMemberRole> = {
    0: "Autor",
    1: "Edytor",
    2: "Opiniujacy",
    3: "Zatwierdzajacy",
    4: "Zatwierdzajacy",
}

const roleByStageType: Record<UiStageType, TeamMemberRole> = {
    EDIT: "Edytor",
    REVIEW: "Opiniujacy",
    APPROVAL: "Zatwierdzajacy",
}

const workflowActionTypeTranslations: Record<string, string> = {
    SENDTOOPINION: "Wyslano do opiniowania",
    FINISHOPINION: "Opiniowanie",
    WITHDRAWFROMOPINION: "Wycofano z opiniowania",
    SENDTOAPPROVE: "Wyslano do zatwierdzenia",
    APPROVE: "Zatwierdzanie",
    REJECT: "Odrzucono dokument",
    FINISHEDIT: "Zakonczono opracowanie",
    FINISHEDITOR: "Zakonczono opracowanie",
    FINISHAPPROVAL: "Zakonczono zatwierdzanie",
}

const normalizeForLookup = (value: string) => {
    return value.toUpperCase().replace(/[\s_-]+/g, "")
}

const normalizeUserName = (value?: string | null) => {
    return (value ?? "").trim().toLowerCase()
}

export const normalizeWorkflowEnum = (value: string) => {
    return normalizeForLookup(value)
}

export const detectStageTypeFromText = (
    name: string,
    description: string
): UiStageType | null => {
    const normalized = `${name} ${description}`.toLowerCase()

    if (normalized.includes("zatwier") || normalized.includes("approv")) {
        return "APPROVAL"
    }

    if (normalized.includes("opini") || normalized.includes("review")) {
        return "REVIEW"
    }

    if (
        normalized.includes("edyc")
        || normalized.includes("oprac")
        || normalized.includes("edit")
    ) {
        return "EDIT"
    }

    return null
}

export const mapStageTypeFromApi = (
    type: DocumentWorkflowStageTypeEnum,
    name: string,
    description: string
): UiStageType => {
    if (typeof type === "string") {
        const mappedType = stageTypeLookup[normalizeForLookup(type)]

        if (mappedType) {
            return mappedType
        }
    }

    if (typeof type === "number") {
        const mappedType = stageTypeByNumber[type]

        if (mappedType) {
            return mappedType
        }
    }

    return detectStageTypeFromText(name, description) ?? "EDIT"
}

export const getStageUiType = (
    stage: DocumentWorkflowStageDto
): UiStageType => {
    return mapStageTypeFromApi(
        stage.type,
        stage.name,
        stage.description
    )
}

export const mapStageTypeToApi = (
    type: Exclude<UiStageType, "EDIT">
): DocumentWorkflowStageTypeEnum => {
    return type === "REVIEW" ? "REVIEW" : "APPROVAL"
}

export const mapStageStatusToUi = (
    status: DocumentWorkflowStageStatusEnum | number | undefined
): UiStageStatusInfo => {
    if (typeof status === "number") {
        if (status === 0) {
            return {
                kind: "pending",
                label: "OCZEKUJE",
                color: "default",
                variant: "outlined",
            }
        }

        if (status === 1) {
            return {
                kind: "inProgress",
                label: "TRWA",
                color: "info",
                variant: "filled",
            }
        }

        if (status === 2) {
            return {
                kind: "completed",
                label: "ZAKONCZONY",
                color: "success",
                variant: "filled",
            }
        }

        if (status === 3) {
            return {
                kind: "cancelled",
                label: "ANULOWANY",
                color: "default",
                variant: "outlined",
            }
        }

        return {
            kind: "unknown",
            label: `Status ${status}`,
            color: "default",
            variant: "outlined",
        }
    }

    if (!status) {
        return {
            kind: "unknown",
            label: "-",
            color: "default",
            variant: "outlined",
        }
    }

    const normalizedStatus = normalizeForLookup(status)

    if (["COMPLETED", "DONE", "FINISHED", "CLOSED", "ZAKONCZONY"].includes(normalizedStatus)) {
        return {
            kind: "completed",
            label: "ZAKONCZONY",
            color: "success",
            variant: "filled",
        }
    }

    if (["ACTIVE", "INPROGRESS", "ONGOING", "CURRENT", "STARTED", "TRWA"].includes(normalizedStatus)) {
        return {
            kind: "inProgress",
            label: "TRWA",
            color: "info",
            variant: "filled",
        }
    }

    if (["NOTSTARTED", "PENDING", "WAITING", "QUEUED", "NEW", "OCZEKUJE"].includes(normalizedStatus)) {
        return {
            kind: "pending",
            label: "OCZEKUJE",
            color: "default",
            variant: "outlined",
        }
    }

    if (["CANCELLED", "CANCELED"].includes(normalizedStatus)) {
        return {
            kind: "cancelled",
            label: "ANULOWANY",
            color: "default",
            variant: "outlined",
        }
    }

    return {
        kind: "unknown",
        label: status,
        color: "default",
        variant: "outlined",
    }
}

export const mapRoleFromApi = (
    role: DocumentTeamMemberRoleEnum | null,
    stageType: UiStageType
): TeamMemberRole => {
    if (typeof role === "string") {
        const mappedRole = roleLookup[normalizeForLookup(role)]

        if (mappedRole) {
            return mappedRole
        }
    }

    if (typeof role === "number") {
        return roleByNumber[role] ?? roleByStageType[stageType]
    }

    return roleByStageType[stageType]
}

export const mapRolesFromApi = (
    roles: DocumentTeamMemberRoleEnum[] | undefined,
    fallbackRole: DocumentTeamMemberRoleEnum | null,
    stageType: UiStageType
): TeamMemberRole[] => {
    const mappedRoles = (roles ?? [])
        .map(role => mapRoleFromApi(role, stageType))
        .filter((role, index, values) => values.indexOf(role) === index)

    if (mappedRoles.length > 0) {
        return mappedRoles
    }

    return [mapRoleFromApi(fallbackRole, stageType)]
}

export const mapRoleToApiFromStageType = (
    stageType: UiStageType
): Exclude<DocumentTeamMemberRoleEnum, number> => {
    if (stageType === "EDIT") {
        return "EDITOR"
    }

    if (stageType === "REVIEW") {
        return "REVIEWER"
    }

    return "APPROVER"
}

export const mapAvailableUser = (
    member: DocumentTeamMemberDto
): AvailableWorkflowUser => ({
    id: member.id,
    name: member.fullName,
    userName: member.userName,
    email: member.userName,
    position: member.position,
    companyName: member.companyName,
})

export const mapMemberFromDto = (
    member: DocumentTeamMemberDto,
    stageType: UiStageType,
    stageAction?: StageActionData
): WorkflowMemberViewModel => ({
    id: member.id,
    name: member.fullName,
    userName: member.userName,
    email: member.userName,
    position: member.position,
    companyName: member.companyName,
    roles: mapRolesFromApi(member.roles, member.role, stageType),
    actionType: stageAction?.actionType,
    actionStatus: stageAction?.actionStatus,
    actionExecDate: stageAction?.actionExecDate,
    actionDeadline: stageAction?.actionDeadline,
})

export const getLatestStageActionForMember = (
    stage: DocumentWorkflowStageDto,
    memberId: number,
    userName: string
) => {
    const normalizedUserName = normalizeUserName(userName)
    const memberActions = stage.actions.filter(action => {
        return action.documentTeamMemberId === memberId
            || normalizeUserName(action.userName) === normalizedUserName
    })

    if (memberActions.length === 0) {
        return null
    }

    return [...memberActions].sort((left, right) => {
        const leftDate = new Date(left.execDate ?? left.createDate).getTime()
        const rightDate = new Date(right.execDate ?? right.createDate).getTime()

        if (Number.isNaN(leftDate) || Number.isNaN(rightDate)) {
            return 0
        }

        return rightDate - leftDate
    })[0]
}

export const getStageMembers = (
    stage: DocumentWorkflowStageDto,
    teamMembers: DocumentTeamMemberDto[]
): WorkflowMemberViewModel[] => {
    const stageType = getStageUiType(stage)
    const membersByUserName = new Map(
        teamMembers.map(member => [normalizeUserName(member.userName), member])
    )

    if (stage.members.length > 0) {
        return stage.members.map(stageMember => {
            const memberFromTeam = membersByUserName.get(
                normalizeUserName(stageMember.userName)
            )

            const latestAction = getLatestStageActionForMember(
                stage,
                stageMember.documentTeamMemberId,
                stageMember.userName
            )

            const actionData: StageActionData = {
                actionType: stageMember.actionType ?? latestAction?.actionType ?? null,
                actionStatus: stageMember.actionStatus ?? latestAction?.status ?? null,
                actionExecDate: stageMember.execDate ?? latestAction?.execDate ?? null,
                actionDeadline: stageMember.deadline ?? latestAction?.deadline ?? null,
            }

            if (memberFromTeam) {
                return {
                    ...mapMemberFromDto(memberFromTeam, stageType, actionData),
                    roles: [mapRoleFromApi(stageMember.role, stageType)],
                }
            }

            return {
                id: stageMember.documentTeamMemberId,
                name: stageMember.fullName,
                userName: stageMember.userName,
                email: stageMember.userName,
                position: stageMember.position,
                companyName: stageMember.companyName,
                roles: [mapRoleFromApi(stageMember.role, stageType)],
                ...actionData,
            }
        })
    }

    const membersById = new Map(
        teamMembers.map(member => [member.id, member])
    )

    const membersFromIds = stage.documentTeamMemberIds
        .map(memberId => membersById.get(memberId))
        .filter((member): member is DocumentTeamMemberDto => Boolean(member))

    const fallbackMembers = membersFromIds.length > 0
        ? membersFromIds
        : teamMembers.filter(member => member.workflowStageIds.includes(stage.id))

    return fallbackMembers.map(member => {
        const latestAction = getLatestStageActionForMember(
            stage,
            member.id,
            member.userName
        )

        return mapMemberFromDto(member, stageType, {
            actionType: latestAction?.actionType ?? null,
            actionStatus: latestAction?.status ?? null,
            actionExecDate: latestAction?.execDate ?? null,
            actionDeadline: latestAction?.deadline ?? null,
        })
    })
}

export const mapWorkflowActionTypeLabel = (
    value?: DocumentWorkflowActionEnum | null
) => {
    if (value === null || value === undefined) {
        return "-"
    }

    if (typeof value === "number") {
        if (value === 0) {
            return "Edycja"
        }

        if (value === 1) {
            return "Opiniowanie"
        }

        if (value === 2) {
            return "Zatwierdzenie"
        }

        return `Typ ${value}`
    }

    const normalizedValue = normalizeForLookup(value)
    const translatedValue = workflowActionTypeTranslations[normalizedValue]

    if (translatedValue) {
        return translatedValue
    }

    if (normalizedValue.includes("EDIT")) {
        return "Edycja"
    }

    if (normalizedValue.includes("REVIEW") || normalizedValue.includes("OPIN")) {
        return "Opiniowanie"
    }

    if (normalizedValue.includes("APPROV") || normalizedValue.includes("ACCEPT")) {
        return "Zatwierdzenie"
    }

    if (normalizedValue.includes("REJECT")) {
        return "Odrzucenie"
    }

    return value
}

export const mapWorkflowActionStatus = (
    value?: DocumentWorkflowActionStatusEnum | null
): UiWorkflowActionStatusInfo => {
    if (value === null || value === undefined) {
        return {
            kind: "unknown",
            label: "-",
            color: "default",
            waiting: false,
        }
    }

    if (typeof value === "number") {
        if (value === 0) {
            return {
                kind: "waiting",
                label: "OCZEKUJE",
                color: "warning",
                waiting: true,
            }
        }

        if (value === 1) {
            return {
                kind: "inProgress",
                label: "W TRAKCIE",
                color: "info",
                waiting: true,
            }
        }

        if (value === 2) {
            return {
                kind: "completed",
                label: "WYKONANO",
                color: "success",
                waiting: false,
            }
        }

        if (value === 3) {
            return {
                kind: "rejected",
                label: "ODRZUCONO",
                color: "error",
                waiting: false,
            }
        }

        return {
            kind: "unknown",
            label: `Status ${value}`,
            color: "default",
            waiting: false,
        }
    }

    const normalizedValue = normalizeForLookup(value)

    if (["PENDING", "WAITING", "NEW", "TODO", "CREATED"].includes(normalizedValue)) {
        return {
            kind: "waiting",
            label: "OCZEKUJE",
            color: "warning",
            waiting: true,
        }
    }

    if (["INPROGRESS", "STARTED", "PROCESSING", "ONGOING"].includes(normalizedValue)) {
        return {
            kind: "inProgress",
            label: "W TRAKCIE",
            color: "info",
            waiting: true,
        }
    }

    if (["DONE", "COMPLETED", "APPROVED", "EXECUTED", "SUCCESS"].includes(normalizedValue)) {
        return {
            kind: "completed",
            label: "WYKONANO",
            color: "success",
            waiting: false,
        }
    }

    if (["REJECTED", "FAILED"].includes(normalizedValue)) {
        return {
            kind: "rejected",
            label: "ODRZUCONO",
            color: "error",
            waiting: false,
        }
    }

    if (["CANCELED", "CANCELLED"].includes(normalizedValue)) {
        return {
            kind: "cancelled",
            label: "ANULOWANO",
            color: "default",
            waiting: false,
        }
    }

    return {
        kind: "unknown",
        label: value,
        color: "default",
        waiting: false,
    }
}

export const isPendingWorkflowActionStatus = (
    value?: DocumentWorkflowActionStatusEnum | null
) => {
    return mapWorkflowActionStatus(value).waiting
}

export const selectCurrentStage = (
    sections: DocumentWorkflowStageDto[],
    currentStageId: number | null
) => {
    return sections.find(stage => stage.isCurrent)
        ?? sections.find(stage => currentStageId !== null && stage.id === currentStageId)
        ?? sections.find(stage => mapStageStatusToUi(stage.status).kind === "inProgress")
        ?? null
}

export const formatDateTime = (value?: string | null) => {
    if (!value) {
        return "-"
    }

    const date = new Date(value)

    if (Number.isNaN(date.getTime())) {
        return "-"
    }

    return date.toLocaleString("pl-PL")
}

export const formatDateOnly = (value?: string | null) => {
    if (!value) {
        return "-"
    }

    const date = new Date(value)

    if (Number.isNaN(date.getTime())) {
        return "-"
    }

    return date.toLocaleDateString("pl-PL")
}

export const isPastDeadline = (deadline?: string | null) => {
    if (!deadline) {
        return false
    }

    const deadlineDate = new Date(deadline).getTime()

    return !Number.isNaN(deadlineDate) && deadlineDate < Date.now()
}

export const getPersonInitials = (name?: string | null) => {
    if (!name) {
        return "?"
    }

    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase())
        .join("")
}

export const createStageTitle = (
    stageType: Exclude<UiStageType, "EDIT">,
    existingCount: number
) => {
    if (stageType === "REVIEW") {
        return existingCount === 0
            ? "Opiniowanie dokumentu"
            : `Opiniowanie dokumentu ${existingCount + 1}`
    }

    return existingCount === 0
        ? "Zatwierdzanie dokumentu"
        : `Zatwierdzanie dokumentu ${existingCount + 1}`
}

export const createStageDescription = (
    stageType: Exclude<UiStageType, "EDIT">
) => {
    if (stageType === "REVIEW") {
        return "Etap zbierania opinii i uwag do dokumentu."
    }

    return "Etap finalnej akceptacji i zatwierdzenia dokumentu."
}